/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VERDAD ÚNICA DE LOCATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El Cloudflare Worker resuelve la ubicación del visitante (país por CF,
 * provincia AR por region code ISO + IPLocate, city canónica vía alias map,
 * barrio por polígonos) y la inyecta en `window.__TEMPLATE_CONFIG__` /
 * cookies. ESTE MÓDULO es la única puerta de entrada para leerla fuera de
 * React; en componentes usá `useTemplateSelection` (misma fuente).
 *
 * Reglas (enforced por scripts/validate-location-single-truth.cjs en build):
 *   - PROHIBIDO detectar país/provincia con timezone o idioma del browser.
 *   - PROHIBIDO llamar proveedores geo (ipapi/ipinfo) desde el cliente.
 *   - El enrichment de /api/geolocate solo COMPLETA campos que el Worker no
 *     resolvió (VPN/ASN) — nunca pisa country/province/city (ver
 *     getWorkerGeoAnalyticsProps en serverTemplateDetection.ts).
 *
 * Consumidores clave: heroCta (membership MP vs Stripe por país), CTA geo,
 * analytics de provincia, SEO region, checkout hints. El país de PAGOS se
 * resuelve server-side con cf-ipcountry (api/create-payment-intent) — esta
 * verdad client-side es para UI/analytics, no para autorizar cobros.
 */

import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { getArgentinaProvinceCodeByName } from "@/config/argentinaProvinces";

export type CanonicalRegion = "LATAM" | "US" | "SPAIN" | "OTHER";

const LATAM_COUNTRIES = new Set([
  "AR", "BO", "BR", "CL", "CO", "CR", "CU", "DO", "EC", "SV",
  "GT", "HN", "MX", "NI", "PA", "PY", "PE", "PR", "UY", "VE",
]);

/** País (ISO2, taxonomía Worker) → región de negocio. null si país desconocido. */
export function regionFromCountry(
  country: string | null | undefined,
): CanonicalRegion | null {
  if (!country || country === "UNKNOWN") return null;
  const c = country.toUpperCase();
  if (c === "US") return "US";
  if (c === "ES") return "SPAIN";
  return LATAM_COUNTRIES.has(c) ? "LATAM" : "OTHER";
}

export interface CanonicalLocation {
  /** ISO2 ("AR", "US", …) o null si el Worker no resolvió. */
  country: string | null;
  /** Nombre completo taxonomía Worker ("CABA", "Buenos Aires", …). */
  province: string | null;
  /** Código del catálogo de templates ("CABA", "BA", "CB", …) — solo AR. */
  provinceCode: string | null;
  city: string | null;
  neighborhood: string | null;
  region: CanonicalRegion | null;
  source: "worker" | "none";
}

const EMPTY: CanonicalLocation = {
  country: null,
  province: null,
  provinceCode: null,
  city: null,
  neighborhood: null,
  region: null,
  source: "none",
};

export function getCanonicalLocation(): CanonicalLocation {
  if (typeof window === "undefined") return EMPTY;
  const cfg = getServerTemplateConfig();
  if (!cfg || !cfg.country || cfg.country === "UNKNOWN") return EMPTY;
  const province = cfg.province?.trim() || null;
  return {
    country: cfg.country,
    province,
    provinceCode:
      cfg.country === "AR" ? getArgentinaProvinceCodeByName(province) : null,
    city: cfg.city?.trim() || null,
    neighborhood: cfg.neighborhood?.trim() || null,
    region: regionFromCountry(cfg.country),
    source: "worker",
  };
}
